import { Button, Card, MessageBox, Table, TableCell, TableHeadCell, TableRow } from "@rothoblaas/design-system";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type FC,
  type RefObject,
  type SetStateAction,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import useLocalStorage from "../../../hooks/useLocalStorage";
import useI18n from "../../../hooks/useTranslations";
import type { BoardQuantity } from "../../../http/deckDesigner/formulas";
import {
  L_SHAPE,
  RECTANGULAR_SHAPE,
  type DifferentBoardPosition,
} from "../../../http/deckDesigner/types/configurations";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import type { PlaneCoordinate } from "./CartesianPlane";
import type { DifferentBoardKey } from "./DeckBoardsLayer";
import styles from "./DeckDrawingCard.module.css";
import { useMeasurementFormat } from "./measurementFormat";
import DeckDrawingPlane, { type DeckShape } from "./DeckDrawingPlane";
import { getDimensionBounds, type DimensionBoundsInput, type DimensionFieldName } from "./dimensionBounds";
import {
  areCoordinatesEqual,
  coordinatesMatchFormDimensions,
  finishLShapeCoordinates,
  finishRectangleCoordinates,
  getBoardSegments,
  getLShapeCoordinates,
  getLShapeDimensions,
  getLShapeGeometry,
  getRectangularCoordinates,
  getRectangularDimensions,
  hasMatchingDimension,
  isPositiveNumber,
} from "./deckGeometry";
import { isSlopedTerrain } from "./deckOutline";

type DeckDrawingCardProps = {
  boardQuantity: BoardQuantity | null;
  className?: string;
  embedded?: boolean;
  onDimensionsCleared?: () => void;
  planeContentRef?: RefObject<HTMLDivElement | null>;
  // When true, hides the interactive grid/axis chrome and fits the whole deck in view (for a clean export snapshot).
  exportMode?: boolean;
};

type CoordinateSource = "form" | "plane";

// Matches the side codes shown by DeckEdgeMeasurements (e.g. "3.20 m ( L1 )").
const SIDE_CODE_FIELD: Record<string, DimensionFieldName> = {
  L1: "base",
  L2: "height",
  l1: "secondaryBase",
  l2: "secondaryHeight",
};

type DifferentBoardWidthItem = {
  label: string;
  value: number;
};

const isDimensionValueValid = (
  fieldName: DimensionFieldName,
  value: number,
  boundsInput: DimensionBoundsInput,
): boolean => {
  const bounds = getDimensionBounds(fieldName, boundsInput);

  return (bounds.min == null || value >= bounds.min) && (bounds.max == null || value <= bounds.max);
};

const PLANE_HINT_DISMISSED_KEY = "deckDesigner.planeHintDismissed";

const DeckDrawingCard: FC<DeckDrawingCardProps> = ({
  boardQuantity,
  className,
  embedded = false,
  onDimensionsCleared,
  planeContentRef,
  exportMode = false,
}) => {
  const i18n = useI18n();
  const measurementFormat = useMeasurementFormat();
  const calculationTranslation = i18n.translation.deckDesignerModule.configurations.calculation;
  const { control, getValues, setValue } = useFormContext<DeckDesignerConfigurationFormValues>();
  const coordinateSourceRef = useRef<CoordinateSource>("form");

  const shape = useWatch({ control, name: "shape", exact: true });
  const base = useWatch({ control, name: "base", exact: true });
  const height = useWatch({ control, name: "height", exact: true });
  const secondaryBase = useWatch({ control, name: "secondaryBase", exact: true });
  const secondaryHeight = useWatch({ control, name: "secondaryHeight", exact: true });
  const lShapeCutoutCorner = useWatch({ control, name: "lShapeCutoutCorner", exact: true });
  const hasHorizontalBoards = useWatch({ control, name: "hasHorizontalBoards", exact: true });
  const boardWidth = useWatch({ control, name: "boardWidth", exact: true });
  const longDifferentBoardWidth = useWatch({ control, name: "longDifferentBoardWidth", exact: true });
  const shortDifferentBoardWidth = useWatch({ control, name: "shortDifferentBoardWidth", exact: true });
  const longDifferentBoardPosition = useWatch({ control, name: "longDifferentBoardPosition", exact: true });
  const shortDifferentBoardPosition = useWatch({ control, name: "shortDifferentBoardPosition", exact: true });
  const gap = useWatch({ control, name: "gap", exact: true });
  const slopeDegrees = useWatch({ control, name: "slopeDegrees", exact: true });
  const slopeArrow = useWatch({ control, name: "slopeArrow", exact: true });

  const selectedShape = shape === RECTANGULAR_SHAPE ? RECTANGULAR_SHAPE : L_SHAPE;

  // Seeded from the form values (not []) so CartesianPlane's mount-time fit-to-bounds effect —
  // a useLayoutEffect, which always runs before this component's own useEffect below — sees the
  // real shape immediately instead of an empty array, which would otherwise lock it into the
  // default (unfitted, real-world-scale) zoom for the rest of the component's lifetime.
  const [coordinates, setCoordinates] = useState<PlaneCoordinate[]>(() =>
    selectedShape === RECTANGULAR_SHAPE
      ? getRectangularCoordinates(base, height)
      : getLShapeCoordinates(base, height, secondaryBase, secondaryHeight, lShapeCutoutCorner),
  );

  const [planeHintDismissed, setPlaneHintDismissed] = useLocalStorage(PLANE_HINT_DISMISSED_KEY, false);
  const [showPlaneHint, setShowPlaneHint] = useState(!planeHintDismissed);
  const isReadOnly = useReadOnly();

  const drawingShape: DeckShape = selectedShape === RECTANGULAR_SHAPE ? "rectangle" : "lShape";
  const drawingPointLimit = selectedShape === RECTANGULAR_SHAPE ? 4 : 6;
  const finishDrawingCoordinates =
    selectedShape === RECTANGULAR_SHAPE ? finishRectangleCoordinates : finishLShapeCoordinates;
  const formCoordinates = useMemo(
    () =>
      selectedShape === RECTANGULAR_SHAPE
        ? getRectangularCoordinates(base, height)
        : getLShapeCoordinates(base, height, secondaryBase, secondaryHeight, lShapeCutoutCorner),
    [base, height, lShapeCutoutCorner, secondaryBase, secondaryHeight, selectedShape],
  );

  useEffect(() => {
    coordinateSourceRef.current = "form";
    setCoordinates((currentCoordinates) =>
      areCoordinatesEqual(currentCoordinates, formCoordinates) ||
      coordinatesMatchFormDimensions(currentCoordinates, selectedShape, base, height, secondaryBase, secondaryHeight)
        ? currentCoordinates
        : formCoordinates,
    );
  }, [base, formCoordinates, height, secondaryBase, secondaryHeight, selectedShape]);

  // The callbacks handed to DeckDrawingPlane are stable while their inputs are, so its memoized
  // layers don't re-render for every render of this card.
  const setPlaneCoordinates = useCallback<Dispatch<SetStateAction<PlaneCoordinate[]>>>((nextCoordinates) => {
    coordinateSourceRef.current = "plane";
    setCoordinates(nextCoordinates);
  }, []);

  const clearDimensionFields = useCallback(() => {
    (["base", "height", "secondaryBase", "secondaryHeight"] as const).forEach((fieldName) =>
      setValue(fieldName, null, { shouldDirty: true, shouldValidate: true }),
    );
    onDimensionsCleared?.();
  }, [onDimensionsCleared, setValue]);

  const openPlaneHint = useCallback(() => setShowPlaneHint(true), []);

  const updateDimensionValue = (fieldName: DimensionFieldName, currentValue: number | null, nextValue: number) => {
    if (hasMatchingDimension(currentValue, nextValue)) {
      return;
    }

    setValue(fieldName, nextValue, { shouldDirty: true, shouldValidate: true });
  };

  const handleDimensionChange = useCallback(
    (sideCode: string, value: number): boolean => {
      const fieldName = SIDE_CODE_FIELD[sideCode];

      if (!fieldName) {
        return false;
      }

      const boundsInput: DimensionBoundsInput = {
        base,
        height,
        secondaryBase,
        secondaryHeight,
      };

      if (!isDimensionValueValid(fieldName, value, boundsInput)) {
        return false;
      }

      if (getValues(fieldName) !== value) {
        setValue(fieldName, value, { shouldDirty: true, shouldValidate: true });
      }

      return true;
    },
    [base, getValues, height, secondaryBase, secondaryHeight, setValue],
  );

  const rectangularDimensions = useMemo(() => getRectangularDimensions(coordinates), [coordinates]);
  const lShapeDimensions = useMemo(() => getLShapeDimensions(coordinates), [coordinates]);
  const lShapeGeometry = useMemo(() => getLShapeGeometry(coordinates), [coordinates]);
  const boardSegments = useMemo(
    () =>
      getBoardSegments(
        coordinates,
        selectedShape,
        boardWidth,
        gap,
        hasHorizontalBoards,
        boardQuantity,
        longDifferentBoardWidth,
        shortDifferentBoardWidth,
        longDifferentBoardPosition,
        shortDifferentBoardPosition,
      ),
    [
      boardQuantity,
      boardWidth,
      coordinates,
      longDifferentBoardWidth,
      longDifferentBoardPosition,
      gap,
      hasHorizontalBoards,
      shortDifferentBoardWidth,
      shortDifferentBoardPosition,
      selectedShape,
    ],
  );
  const updateDifferentBoardPosition = useCallback(
    (key: DifferentBoardKey, position: DifferentBoardPosition) => {
      const fieldName = key === "long" ? "longDifferentBoardPosition" : "shortDifferentBoardPosition";

      setValue(fieldName, position, { shouldDirty: true });
    },
    [setValue],
  );
  const boardWidthFormatter = useMemo(
    () =>
      new Intl.NumberFormat(i18n.translation.languageCode ?? undefined, {
        maximumFractionDigits: 0,
      }),
    [i18n.translation.languageCode],
  );
  // One row per board with a different width: a rectangle only has the long one, an L can have both.
  const differentBoardWidthRows = useMemo((): DifferentBoardWidthItem[] => {
    if (selectedShape === RECTANGULAR_SHAPE) {
      return isPositiveNumber(longDifferentBoardWidth)
        ? [{ label: calculationTranslation.differentBoard, value: longDifferentBoardWidth }]
        : [];
    }

    return [
      isPositiveNumber(longDifferentBoardWidth)
        ? { label: calculationTranslation.longDifferentBoardWidth, value: longDifferentBoardWidth }
        : null,
      isPositiveNumber(shortDifferentBoardWidth)
        ? { label: calculationTranslation.shortDifferentBoardWidth, value: shortDifferentBoardWidth }
        : null,
    ].filter((item): item is DifferentBoardWidthItem => item != null);
  }, [
    calculationTranslation.differentBoard,
    calculationTranslation.longDifferentBoardWidth,
    calculationTranslation.shortDifferentBoardWidth,
    longDifferentBoardWidth,
    selectedShape,
    shortDifferentBoardWidth,
  ]);
  useEffect(() => {
    if (
      coordinateSourceRef.current !== "plane" ||
      selectedShape !== RECTANGULAR_SHAPE ||
      coordinates.length !== 4 ||
      rectangularDimensions.width == null ||
      rectangularDimensions.height == null
    ) {
      return;
    }

    const boundsInput: DimensionBoundsInput = {
      base: rectangularDimensions.width,
      height: rectangularDimensions.height,
      secondaryBase: null,
      secondaryHeight: null,
    };

    if (
      !isDimensionValueValid("base", rectangularDimensions.width, boundsInput) ||
      !isDimensionValueValid("height", rectangularDimensions.height, boundsInput)
    ) {
      coordinateSourceRef.current = "form";
      setCoordinates(formCoordinates);
      return;
    }

    updateDimensionValue("base", base, rectangularDimensions.width);
    updateDimensionValue("height", height, rectangularDimensions.height);
  }, [
    base,
    coordinates.length,
    formCoordinates,
    height,
    rectangularDimensions.height,
    rectangularDimensions.width,
    selectedShape,
  ]);

  useEffect(() => {
    if (
      coordinateSourceRef.current !== "plane" ||
      selectedShape !== L_SHAPE ||
      coordinates.length !== 6 ||
      lShapeDimensions.width == null ||
      lShapeDimensions.height == null ||
      lShapeDimensions.secondaryWidth == null ||
      lShapeDimensions.secondaryHeight == null
    ) {
      return;
    }

    const boundsInput: DimensionBoundsInput = {
      base: lShapeDimensions.width,
      height: lShapeDimensions.height,
      secondaryBase: lShapeDimensions.secondaryWidth,
      secondaryHeight: lShapeDimensions.secondaryHeight,
    };

    if (
      !isDimensionValueValid("base", lShapeDimensions.width, boundsInput) ||
      !isDimensionValueValid("height", lShapeDimensions.height, boundsInput) ||
      !isDimensionValueValid("secondaryBase", lShapeDimensions.secondaryWidth, boundsInput) ||
      !isDimensionValueValid("secondaryHeight", lShapeDimensions.secondaryHeight, boundsInput)
    ) {
      coordinateSourceRef.current = "form";
      setCoordinates(formCoordinates);
      return;
    }

    updateDimensionValue("base", base, lShapeDimensions.width);
    updateDimensionValue("height", height, lShapeDimensions.height);
    updateDimensionValue("secondaryBase", secondaryBase, lShapeDimensions.secondaryWidth);
    updateDimensionValue("secondaryHeight", secondaryHeight, lShapeDimensions.secondaryHeight);
    // Which corner is cut is saved too (in the outline), so a drawn L keeps its orientation on reload.
    if (lShapeGeometry && lShapeGeometry.cutoutCorner !== lShapeCutoutCorner) {
      setValue("lShapeCutoutCorner", lShapeGeometry.cutoutCorner, { shouldDirty: true });
    }
  }, [
    base,
    coordinates.length,
    formCoordinates,
    height,
    lShapeCutoutCorner,
    lShapeGeometry,
    lShapeDimensions.height,
    lShapeDimensions.secondaryHeight,
    lShapeDimensions.secondaryWidth,
    lShapeDimensions.width,
    secondaryBase,
    secondaryHeight,
    selectedShape,
  ]);

  const content = (
    <div className={styles.content}>
      <div className={styles.planeWrapper}>
        <DeckDrawingPlane
          boardSegments={boardSegments}
          boardWidth={boardWidth}
          coordinates={coordinates}
          exportMode={exportMode}
          readOnly={isReadOnly}
          finishCoordinates={finishDrawingCoordinates}
          hasHorizontalBoards={hasHorizontalBoards}
          onClear={clearDimensionFields}
          onDifferentBoardPositionChange={updateDifferentBoardPosition}
          onDimensionChange={handleDimensionChange}
          onShowHint={
            // The hint explains how to draw and edit, so a viewer has no use for it.
            exportMode || isReadOnly ? undefined : openPlaneHint
          }
          planeContentRef={planeContentRef}
          pointLimit={drawingPointLimit}
          setCoordinates={setPlaneCoordinates}
          shape={drawingShape}
          slopeArrow={isSlopedTerrain({ slopeDegrees, slopeArrow }) ? slopeArrow : null}
        />
        {showPlaneHint && !exportMode && !isReadOnly && (
          <div className={styles.planeHintOverlay}>
            <p className={styles.planeHintText}>{calculationTranslation.planeInteractionHint}</p>
            <ul className={styles.planeHintList}>
              <li>{calculationTranslation.planeInteractionFeatureDraw}</li>
              <li>{calculationTranslation.planeInteractionFeatureLength}</li>
              <li>{calculationTranslation.planeInteractionFeatureDrag}</li>
              <li>{calculationTranslation.planeInteractionFeatureEditLabel}</li>
              <li>{calculationTranslation.planeInteractionFeatureDifferentBoards}</li>
              <li>{calculationTranslation.planeInteractionFeatureClear}</li>
            </ul>
            <Button
              className={styles.planeHintStartButton}
              onClick={() => {
                setPlaneHintDismissed(true);
                setShowPlaneHint(false);
              }}
            >
              {/* already dismissed once: the tutorial was reopened from its icon */}
              {planeHintDismissed
                ? calculationTranslation.planeInteractionContinue
                : calculationTranslation.planeInteractionStart}
            </Button>
          </div>
        )}
      </div>
      {differentBoardWidthRows.length > 0 && (
        <div className={styles.differentBoardWidths}>
          <Table dense>
            <thead>
              <TableRow>
                <TableHeadCell align="left">{calculationTranslation.differentBoardsColumn}</TableHeadCell>
                <TableHeadCell align="right">{i18n.translation.common.width}</TableHeadCell>
              </TableRow>
            </thead>
            <tbody>
              {differentBoardWidthRows.map((row) => (
                <TableRow key={row.label}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell className={styles.numericCell}>
                    {measurementFormat.length(
                      row.value,
                      "mm",
                      `${boardWidthFormatter.format(row.value)} ${i18n.translation.common.unitMm}`,
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
          <MessageBox type="informative" content={calculationTranslation.differentBoardWidthDragHint} />
        </div>
      )}
    </div>
  );

  return embedded ? (
    content
  ) : (
    <Card
      style={{ minHeight: 400 }}
      className={className}
      accentColor="var(--accent-base, var(--red))"
      shadow
      title={i18n.translation.common.titlePreview}
    >
      {content}
    </Card>
  );
};

// Memoized: its props are stable, so it re-renders through its own watched fields and props only, not
// whenever its parent does.
export default memo(DeckDrawingCard);
